import fse from 'fs-extra';
import { Guid } from 'guid-typescript';
import pathExists from 'path-exists';
import { Common } from '../common';
import { cleanFile, updateShellReferences } from '../angular15/utils/update-shell-references';

export class ExtensionCreator {
    /**
     * Arguments:
     *	--company - name of the company or person making the extension.
     *	--tool - name of the tool / extension that is being built.
     *  --solution - name of the solution / extension that is being built.
     *	--version - tag of the version to use.
     *  --internal - instead of referring to SDK, references point to internal shell library
     * @param {any} argv Object containing command arguments
    */
    constructor(private argv: any) { }

    public create(): void {
        const normalizedCompany = this.normalizeString(this.argv.company ? this.argv.company : '');
        const normalizedTool = this.normalizeString(this.argv.tool ? this.argv.tool : '');

        const version = this.argv.version ? this.argv.version.toLowerCase() : '';
        const extensionType = this.argv.solution ? 'solution' : 'tool';
        const normalizedSolution = this.argv.solution ? this.normalizeString(this.argv.solution) : '';
        const internal = this.argv.internal;

        if (this.argv.length === 0 || !this.isValidVersion(version) || !normalizedCompany || !normalizedTool) {
            console.error('Usage: wac create --company <company-name> --name <tool-name> --version <version-tag> [--verbose]');
            console.log('or');
            console.log('wac create --company <company-name> --solution <solution-name> --tool <tool-name> --type <tool-type> --version <version-tag> [--verbose]');
            console.log('Valid version tags: \'latest\', \'insider\', \'next\', \'experimental\'');
            console.log('More information can be found here:');
            process.exit(1);
        }

        if (normalizedSolution === '') {
            this.createHelper(extensionType, normalizedCompany, normalizedTool, '', version, internal);
        } else {
            this.createHelper(extensionType, normalizedCompany, normalizedSolution, normalizedTool, version, internal);
        }
    }

    private createHelper(
        type: string,
        company: string,
        primaryDisplayName: string,
        secondaryDisplayName: string,
        version: string,
        internal: boolean
    ): void {
        const ignoresPath = Common.cliRootPath + 'templates\\ignores';
        const internalPath = Common.cliRootPath + 'templates\\internal';
        const solutionPath = Common.cliRootPath + 'templates\\solution';
        const templatePath = Common.cliRootPath + 'templates\\wac-template';
        const gulpPath = Common.moduleTemplatePath;

        if (pathExists.sync(primaryDisplayName)) {
            console.error('This tool definition already exists.  No changes have been made.');
        } else {
            const productPath = './' + primaryDisplayName;
            fse.mkdirSync(primaryDisplayName);
            console.log('Created ' + productPath);

            fse.copySync(templatePath, productPath);
            fse.copySync(gulpPath + '\\gulpfile.ts', productPath + '\\gulpfile.ts');
            fse.copySync(gulpPath + '\\build', productPath + '\\build');
            fse.copyFileSync(gulpPath + '\\.eslintrc.json', productPath + '\\.eslintrc.json');
            fse.copyFileSync(templatePath + '\\gulpfile.ts\\config-data.ts', productPath + '\\gulpfile.ts\\config-data.ts');
            fse.copyFileSync(ignoresPath + '\\git.txt', productPath + '\\.gitignore');
            fse.copyFileSync(ignoresPath + '\\npm.txt', productPath + '\\.npmignore');
            fse.copyFileSync(ignoresPath + '\\browserslistrc.txt', productPath + '\\.browserslistrc');

            // For solution extensions, overwrite manifest and strings file
            if (type === 'solution') {
                fse.copyFileSync(solutionPath + '\\manifest.json', productPath + '\\src\\manifest.json');
                fse.copyFileSync(solutionPath + '\\strings.resjson', productPath + '\\src\\resources\\strings\\strings.resjson');
            }

            // For internal developers, overwrite package.json and add .npmrc
            if (internal) {
                fse.copyFileSync(internalPath + '\\package.json', productPath + '\\package.json');
                fse.copyFileSync(internalPath + '\\npmrc.txt', productPath + '\\.npmrc');
            }

            this.updateShellReferences(primaryDisplayName, internal);
            this.updateFiles(company, primaryDisplayName, secondaryDisplayName, version, internal);
            this.printOutro(primaryDisplayName);
        }
    }

    private updateShellReferences(primaryDisplayName: string, internal: boolean): void {
        updateShellReferences(primaryDisplayName, internal);
    }

    private updateFiles(
        company: string,
        primaryDisplayName: string,
        secondaryDisplayName: string,
        version: string,
        internal: boolean
    ): void {
        /*
        / files that need updating:
        /	root package.json
        /	src/manifest.json
        /	src/resources/strings/strings.resjson
        /	src/main.ts
        /	in Experimental: gulpfile.js
        */
        const cleanDirectory = {};
        const rootPackagePath = './' + primaryDisplayName + '/package.json';
        const manifestFilePath = './' + primaryDisplayName + '/src/manifest.json';
        const mainFilePath = './' + primaryDisplayName + '/src/main.ts';
        const stringsFilePath = './' + primaryDisplayName + '/src/resources/strings/strings.resjson';
        const gulpFilePath = './' + primaryDisplayName + '/gulpfile.ts/config-data.ts';

        const packageName = '@' + company.toLowerCase() + '/' + primaryDisplayName.toLowerCase();
        const manifestName = company.toLowerCase() + '.' + primaryDisplayName.toLowerCase();
        const stringsProduct = primaryDisplayName.split('-').join(''); // Strings file cannot handle dashes.
        const stringsCompany = company.split('-').join('');
        const companyPackageIdentifier = company.split('-').join('') + primaryDisplayName.split('-').join('');

        /*
        / Default version is 'latest' in wac-template/package.json
        */
        if (version === 'next' || version === 'insider' || version === 'experimental') {
            const existingVersion = '"@microsoft/windows-admin-center-sdk": "latest",';
            cleanDirectory[rootPackagePath] = {
                '@{!company-name}/{!product-name}': packageName,
                '"@microsoft/windows-admin-center-sdk": "latest",': existingVersion.replace('latest', version)
            };
        } else {
            cleanDirectory[rootPackagePath] = { '@{!company-name}/{!product-name}': packageName };
        }

        if (!internal) {
            this.cleanInternalPackages(rootPackagePath);
        }

        cleanDirectory[gulpFilePath] = {
            '{!company-name}.{!module-name}': manifestName,
            '{!CompanyName}{!ProductName}': stringsCompany + stringsProduct,
            '{!guid}': Guid.create(),
            '{!company-package-id}': companyPackageIdentifier
        };

        cleanDirectory[manifestFilePath] = {
            '{!company-name}.{!module-name}': manifestName,
            '{!company-name}.{!product-name}': manifestName,
            '{!primary-display-name}': primaryDisplayName,
            '{!primary-url-name}': primaryDisplayName.toLowerCase(),
            '{!secondary-display-name}': secondaryDisplayName,
            '{!secondary-url-name}': secondaryDisplayName.toLowerCase()
        };

        cleanDirectory[stringsFilePath] = {
            '{!product-display-name}': stringsProduct,
            '{!product-title}': stringsProduct,
            '{!primary-display-name}': primaryDisplayName,
            '{!secondary-display-name}': secondaryDisplayName,
            '{!ProductName}': stringsProduct,
            '{!CompanyName}': stringsCompany
        };

        cleanDirectory[mainFilePath] = { '{!company-name}.{!product-name}': manifestName };

        for (const key in cleanDirectory) {
            if (Object.prototype.hasOwnProperty.call(cleanDirectory, key)) {
                this.cleanFile(key, cleanDirectory[key]);
            }
        }
    }

    private cleanFile(key: string, values: any): void {
        cleanFile(key, values);
    }

    private cleanInternalPackages(packagesFilePath: string): void {
        const packages = Common.readFileJSON(packagesFilePath);
        const dependencies = [packages.peerDependencies, packages.devDependencies];

        const shellPackageFile = Common.cliRootPath + 'src\\config\\shell-package-list.json';
        const shellPackages = Common.readFileJSON(shellPackageFile).packages;

        dependencies.forEach((target) => {
            for (const pack of shellPackages) {
                delete target[pack];
            }
        });

        fse.writeJSONSync(packagesFilePath, packages, { spaces: 2 });
    }

    private printOutro(product: string): void {
        console.log('');
        console.log('Thank you for using the Windows Admin Center CLI.');
        console.log('');
        console.log('Next steps:');
        console.log('cd into your new directory (cd ' + product + ') and then run \'npm install\' -> \'gulp build\' to build your new extension');
        console.log('After that, \'gulp serve --port 4200\' to serve your new extension on port 4200');
        console.log('Additional information can be found here: https://aka.ms/wacsdkdocs');
    }

    private normalizeString(input: string): string {
        return input.split(' ').join('-');
    }

    private isValidVersion(version: string): boolean {
        return version === 'latest' || version === 'next' || version === 'insider' || version === 'release' || version === '' || version === 'experimental';
    }
}
